import React from 'react';
import { get, orderBy } from 'lodash';
import { connect } from 'react-redux';
import { toolTipPointFormatterGenerator, labelFormatterGenerator, toolTipForGroupedBarChartFormatterGenerator } from 'helpers/charts';
import { getSettings } from 'helpers/youtube';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchChannel, fetchChannelCallouts, requestChannelUpsell, showedChannelUpsell } from 'services/channel/duck';
import { fetchYouTubeCampaignSummary, fetchYouTubeAdgroupSummary, fetchYouTubeCreativeSummary } from 'services/youtube/duck';
import { validateStatisticsData } from 'helpers/validate';

// Components and Containers
import LayerThreeDashboardComponent from 'containers/dashboards/DashboardBaseComponent';
import { Grid, Row } from 'react-bootstrap';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import TileWithValidation from 'components/molecules/TileWithValidation';
import RenderingTile from 'components/molecules/RenderingTile';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import ExportPDF from 'containers/buttons/ExportPDF';
import ChannelUpsell from 'components/molecules/ChannelUpsell';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';

// Charts
import GroupedBarChartWithCategories from 'components/molecules/charts/GroupedBarChartWithCategories';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import StackedAreaChart from 'components/molecules/charts/StackedAreaChart';

// Tables
import YoutubePivotTable from 'components/molecules/tables/YoutubePivotTable';
import YoutubeCreativePerformanceTable from 'components/molecules/tables/YoutubeCreativePerformanceTable';

class Dashboard extends LayerThreeDashboardComponent {
    constructor(props) {
        super(props, 'youtube');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();

        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }
        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchYouTubeCampaignSummary(payload);
        this.props.fetchYouTubeAdgroupSummary(payload);
        this.props.fetchYouTubeCreativeSummary(payload);
    }

    render() {
        const stub = `l3-${this.getCurrentChannel()}`;
        const {
            backfillBuckets,
            xAxisLabel,
            selectedTimeRange,
            hideBudgetTimeCharts,
            showConversionRevenue
        } = this.props;

        const isChannelAvailable = get(this.props, 'entities.meta.channelAvailability.youtube', false);

        const hasCalloutData = get(this.props, 'channel.callouts.params.hasData', false);
        const hasAdgroupSummaryData = get(this.props, 'adgroupSummary.hasData', false);
        const hasCreativeSummaryData = get(this.props, 'creativeSummary.hasData', false);
        const hasCampaignSummaryData = get(this.props, 'campaignSummary.hasData', false);
        const hasChannelData = get(this.props, 'channel.hasData', false);

        const callouts = get(this.props, 'channel.callouts.data', {});
        const calloutWindow = get(this.props, 'channel.callouts.params.timeRanges.previous.windowSize', null);
        const data = get(this.props, 'channel.data', {});

        const campaignSummaryData = get(this.props, 'campaignSummary.data', []);
        const sortedCampaignSummaryData =
            hasCampaignSummaryData ? orderBy(campaignSummaryData, ['video_views'], ['desc']) : [];

        const adgroupSummaryData = get(this.props, 'adgroupSummary.data', []);
        const sortedAdgroupSummaryData =
            hasAdgroupSummaryData ? orderBy(adgroupSummaryData, ['video_views'], ['desc']) : [];

        const creativeSummaryData = get(this.props, 'creativeSummary.data', []);

        const upsellSettings = this.props.entityMeta.upsellSettings;
        const upsellRequested = get(this.props, 'entityMeta.upsellRequested', {});

        const hasVideoViews = hasChannelData ? !!get(this.props, 'channel.statistics.video_views') : null;

        const { calloutSettings, twoAxisChart, commonKeys, tableType } = getSettings(hasVideoViews);

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow
        });

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    YouTube
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs     */}
                <Row>
                    {calloutComponents}
                </Row>
                {/* Charts */}
                {hasChannelData && hasVideoViews !== null ? (
                    <TwoAxisBarWithLineChart
                        title={twoAxisChart.title}
                        exportName={`${stub}-two-axis-chart-1`}
                        data={data}
                        yBar={twoAxisChart.yBar}
                        yBarTitle={twoAxisChart.yBarTitle}
                        yBarOptions={{ formatByType: 'number' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yLine={twoAxisChart.yLine}
                        yLineTitle={twoAxisChart.yLineTitle}
                        yLineOptions={{ formatByType: 'percentage' }}
                        yAxisLineOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('percentage')
                            }
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData && hasVideoViews !== null} />
                )}

                {hasCampaignSummaryData && hasVideoViews !== null ? (
                    <GroupedBarChartWithCategories
                        title="TOP CAMPAIGN VIEW PERFORMANCE"
                        exportName={`${stub}-campaign-view-performance`}
                        data={sortedCampaignSummaryData}
                        showTopOnly
                        keys={commonKeys}
                        partitionBy="campaign_name"
                        filterField='clicks'
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={hasCampaignSummaryData && hasVideoViews !== null} />
                )}

                {hasAdgroupSummaryData && hasVideoViews !== null ? (
                    <GroupedBarChartWithCategories
                        title="TOP AD GROUP VIEW PERFORMANCE"
                        exportName={`${stub}-adgroup-view-performance`}
                        data={sortedAdgroupSummaryData}
                        showTopOnly
                        keys={commonKeys}
                        // eslint-disable-next-line func-names
                        tooltipFormatter={toolTipForGroupedBarChartFormatterGenerator(
                            {
                                data: sortedAdgroupSummaryData,
                                field: 'campaign_name',
                                label: 'Campaign'
                            })}
                        partitionBy="adgroup_name"
                        filterField='clicks'
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={hasAdgroupSummaryData && hasVideoViews !== null} />
                )}

                {hasAdgroupSummaryData ? (
                    <YoutubePivotTable
                        exportName={`${stub}-performance-overview`}
                        hasData={hasAdgroupSummaryData}
                        data={adgroupSummaryData}
                        tableType={tableType}
                        showConversionRevenue={showConversionRevenue}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasAdgroupSummaryData} />
                )}

                <YoutubeCreativePerformanceTable
                    exportName={`${stub}-creative-performance`}
                    hasData={hasCreativeSummaryData}
                    data={creativeSummaryData}
                    showConversionRevenue={showConversionRevenue}
                />

                <TileWithValidation
                    hasData={hasChannelData && !hideBudgetTimeCharts}
                    innerComponent={StackedAreaChart}
                    innerProps={{
                        title: 'BUDGET REPORT',
                        exportName: `${stub}-budget-report`,
                        data: { [this.state.channel]: data },
                        y: 'spend',
                        xAxisLabel,
                        backfillBuckets,
                        yAxisLabel: 'Budget',
                        yOptions: { formatByType: 'priceWithoutSpace' },
                        toolTipOptions: { pointFormatter: toolTipPointFormatterGenerator },
                        yAxisOptions: {
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        },
                        animateDuration: 2000,
                        xAxisType: 'datetime',
                        validation: {
                            key: 'spend',
                            data: this.props.channel.statistics,
                            validator: validateStatisticsData
                        }
                    }}
                />
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    YouTube
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {upsellSettings.enabled && upsellSettings.youtube ? (
                    <ChannelUpsell
                        channel='youtube'
                        channelDisplay='YouTube'
                        icon='icon-youtube-fill'
                        successfulRequest={upsellRequested.youtube}
                        buttonClick={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.requestChannelUpsell({ entityId, channel });
                        }}
                        onMount={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.showedChannelUpsell({ entityId, channel });
                        }}
                    />
                ) : (
                    <NoChannelDataTile channel='YouTube' />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityMeta: state.entities.meta,
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    channel: state.channel.youtube,
    campaignSummary: get(state, 'youtube.campaign.summary'),
    adgroupSummary: get(state, 'youtube.adgroup.summary'),
    creativeSummary: get(state, 'youtube.creative.summary'),
    conversionSummary: get(state, 'youtube.conversion.summary'),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    activities: state.activities.youtube,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    hideBudgetTimeCharts: get(state, 'entities.meta.meta.hideBudgetTimeCharts', false),
});

export default connect(mapStateToProps, {
    fetchEntityMeta,
    fetchChannel,
    fetchChannelCallouts,
    fetchYouTubeCampaignSummary,
    fetchYouTubeAdgroupSummary,
    fetchYouTubeCreativeSummary,
    requestChannelUpsell,
    showedChannelUpsell,
})(Dashboard);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerThreeYoutubeContainer.js